import { Check, Loader, Minus } from 'lucide-react'
import { FC, InputHTMLAttributes, useEffect, useRef } from 'react'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '~/@/components/ui/tooltip'
import { cn } from '~/@/lib/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

export interface CustomSelectionsCheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  indeterminate?: boolean
  isProcessing?: boolean
  processingButtonName?: string | null
}

export const CustomSelectionsCheckbox: FC<CustomSelectionsCheckboxProps> = ({
  indeterminate = false,
  isProcessing = false,
  processingButtonName,
  className,
  checked,
  disabled,
  ...props
}) => {
  const { t } = useTypedTranslation()
  const internalRef = useRef<HTMLInputElement>(null)
  const checkboxRef = internalRef

  // Set indeterminate state on the native checkbox
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate
    }
  }, [checkboxRef, indeterminate])

  if (isProcessing && processingButtonName) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative inline-flex cursor-default items-center justify-center">
            <div className="m-px flex size-[14px] shrink-0 items-center justify-center rounded-[2px] border-0 bg-transparent">
              <Loader className="pointer-events-none size-4 animate-spin text-gray-11 [animation-duration:2s]" />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            align="center"
            avoidCollisions={true}
            className="z-[9999]"
            collisionPadding={8}
            side="top"
            sideOffset={8}
          >
            {t('table_settings.button_node.action_running', {
              actionName: `${processingButtonName} `,
            })}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    )
  }

  const isChecked = checked || false
  const showIcon = isChecked || indeterminate

  return (
    <div className="relative inline-flex items-center justify-center">
      <input
        ref={checkboxRef}
        className="peer absolute size-4 cursor-pointer opacity-0 disabled:cursor-default"
        type="checkbox"
        {...props}
      />
      <div
        className={cn(
          'm-px flex size-[14px] shrink-0 items-center justify-center rounded-[2px] transition-colors',
          showIcon
            ? 'border border-gray-12 bg-gray-12'
            : 'border border-gray-6 bg-white peer-hover:border-gray-6 peer-hover:bg-gray-1',
          disabled && 'border-gray-4 bg-gray-1 pointer-events-none',
          disabled && showIcon && 'bg-gray-4',
        )}
      >
        {showIcon && (
          <div className="flex items-center justify-center text-white">
            {indeterminate ? <Minus className="size-3" /> : <Check className="size-3" />}
          </div>
        )}
      </div>
    </div>
  )
}
